import React from 'react';
import { Button } from '../components/ui';

/**
 * Shown when a SEASON team reaches the shared player-detail route.
 *
 * WHY THIS EXISTS (2026-08-16, PR review).
 * `PlayerPage` is a WEEKLY-ONLY screen end to end, and clicking any season
 * roster card sent managers straight into it:
 *
 *   • its load path calls `getTeamData(teamId)` → `GET /v2/teams/<id>/stats/`
 *     → `TeamStatsView.get()`, which calls `assert_weekly_league()`
 *     UNCONDITIONALLY (backend/game/api/views/stats.py:102) and rejects a
 *     season team outright;
 *   • `getTeamData()` swallows that non-2xx and RETURNS null, so the page threw
 *     its generic "Failed to load team data" — the user never learned that
 *     player details simply aren't a season feature yet; and
 *   • that error screen's only control is `onNavigate('HOME')`, which
 *     `handlePlayerPageNavigation` did not recognise, so the sole way out was a
 *     browser reload.
 *
 * WHY THE ROSTER SWAP ALONE WOULD NOT FIX IT — loading the season roster gets a
 * NAME onto the screen and nothing else. Everything the page is FOR is weekly:
 *
 *   • the GEAR tab reads `getPlayerEquipment(character_sheet_id)`, and the
 *     season roster contract omits `character_sheet_id` — the same field that
 *     already gates season gear;
 *   • SCORE / PROJ and the PTS breakdown come from weekly `WeeklyPlayerStats`
 *     via that same rejected endpoint; and
 *   • its GEAR button routes to the gear page, which is itself gated.
 *
 * So a "working" season PlayerPage would be a name, a default avatar and four
 * empty panels. Stating the limitation is more honest than rendering that.
 *
 * WHY A DEDICATED SCREEN RATHER THAN AN AUTO-REDIRECT: `currentScreen` persists
 * in localStorage, so a user whose tab closed on `playerPage` is restored here
 * without clicking anything. Bouncing them elsewhere silently would read as the
 * app losing their place.
 *
 * DELETE THIS FILE when season player detail is actually designed and built —
 * this is a genuinely unbuilt feature, not a one-line serializer gap. See
 * AIDocs/Proposed/backend/season-player-detail-contract.md
 */
function SeasonPlayerUnavailable({ onBack }) {
    if (typeof onBack !== 'function') {
        throw new Error(
            '❌ SeasonPlayerUnavailable requires an onBack handler — this screen is a ' +
            'dead end without one.'
        );
    }

    return (
        <div className="season-team-error" role="status">
            <h2>Player Details Aren’t Ready for Campaigns Yet</h2>
            <p className="season-team-error-message">
                Individual player pages are still a daily-league feature. Your
                campaign roster, lineup and results all work normally — this one
                screen just doesn’t have season data behind it yet.
            </p>
            <Button onClick={onBack} variant="secondary">
                Back to My Campaign
            </Button>
        </div>
    );
}

export default SeasonPlayerUnavailable;
